<template>
  <div
    aria-label="Création d’un bon de remboursement logistique"
    role="region"
  >
    <div
      v-if="isLoading === true"
      class="text-center mb-2"
    >
      <b-spinner
        variant="primary"
        label="Chargement en cours"
      />
    </div>
    <b-card>
      <b-form-group
        label="Fournisseur logistique"
        aria-label="Sélection du fournisseur logistique"
      >
        <validation-provider
          #default="{ errors }"
          name="Fournisseur"
        >
          <v-select
            v-model="supplier"
            :clearable="false"
            placeholder="Sélectionner un fournisseur logistique..."
            aria-label="Liste des fournisseurs logistiques"
            label="username"
            :options="suppliers"
            @input="getOrders"
          />
          <small class="text-danger">{{ errors[0] }}</small>
        </validation-provider>
      </b-form-group>
      <b-form-group
        label="Référence du colis"
        label-for="vi-colis-ref"
        aria-label="Saisie de la référence du colis"
      >
        <b-input-group class="input-group-merge">
          <b-input-group-prepend is-text>
            <feather-icon icon="MaximizeIcon" />
          </b-input-group-prepend>
          <b-form-input
            ref="select"
            v-model="ref"
            placeholder="Entrer la référence du colis à rembourser"
            aria-label="Entrer la référence du colis à rembourser"
            @change="checkOrder"
          />
        </b-input-group>
      </b-form-group>
    </b-card>

    <!-- table -->
    <span
      id="table-desc"
      class="sr-only"
    >Tableaux listant les colis disponibles et sélectionnés pour le bon de remboursement. Utilisez les boutons d'action pour déplacer les colis entre les listes.</span>
    <div
      class="d-flex justify-content-between"
      aria-describedby="table-desc"
    >
      <b-card>
        <h4>Colis disponibles à rembourser : {{ orders.length }}</h4>
        <vue-good-table
          aria-label="Tableau des colis disponibles à rembourser"
          role="table"
          :columns="columns"
          :rows="processedOrders"
          :search-options="{
            enabled: true,
            externalQuery: searchTerm }"
        >
          <template
            slot="table-row"
            slot-scope="props"
          >
            <span v-if="props.column.field === 'id'">
              <span> {{ props.row.id }}</span><br>
              <span
                v-if="props.row.is_an_exchange"
                style="color:red ; font-weight: bold;"
                aria-label="Colis d'échange"
              > Échange </span>
            </span>
            <!-- Column: Action -->
            <span v-else-if="props.column.field === 'action'">
              <span>
                <feather-icon
                  :id="`invoice-row-${props.row.id}-show-icon`"
                  aria-label="Ajouter le colis à la liste des colis à rembourser"
                  icon="CheckSquareIcon"
                  class="cursor-pointer mr-1"
                  size="16"
                  color="green"
                  @click="addItem(props.row)"
                />
                <b-tooltip
                  title="Ajouter"
                  class="cursor-pointer"
                  :target="`invoice-row-${props.row.id}-show-icon`"
                />

              </span>
            </span>

            <!-- Column: Common -->

            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>

          <!-- pagination -->
          <template
            slot="pagination-bottom"
            slot-scope="props"
          >
            <div class="d-flex justify-content-between flex-wrap">
              <div class="d-flex align-items-center mb-0 mt-1">
                <span class="text-nowrap">
                  Affichage 1 à
                </span>
                <b-form-select
                  v-model="pageLength"
                  :options="['50','100','500']"
                  class="mx-1"
                  aria-label="Nombre de lignes par page"
                  @input="(value)=>props.perPageChanged({currentPerPage:value})"
                />
                <span class="text-nowrap "> de {{ props.total }} résultats trouvés </span>
              </div>
              <div>
                <b-pagination
                  :value="1"
                  :total-rows="props.total"
                  :per-page="pageLength"
                  first-number
                  last-number
                  align="right"
                  prev-class="prev-item"
                  next-class="next-item"
                  class="mt-1 mb-0"
                  @input="(value)=>props.pageChanged({currentPage:value})"
                >
                  <template #prev-text>
                    <feather-icon
                      icon="ChevronLeftIcon"
                      size="18"
                    />
                  </template>
                  <template #next-text>
                    <feather-icon
                      icon="ChevronRightIcon"
                      size="18"
                    />
                  </template>
                </b-pagination>
              </div>
            </div>
          </template>
        </vue-good-table>
      </b-card>
      <b-card class="ml-1">
        <h4>Colis à rembourser sélectionnés : {{ selected.length }}</h4>
        <vue-good-table
          aria-label="Tableau des colis à rembourser sélectionnés"
          role="table"
          :columns="columns"
          :rows="selected"
          :search-options="{
            enabled: true,
            externalQuery: searchTerm }"
        >
          <template
            slot="table-row"
            slot-scope="props"
          >
            <span v-if="props.column.field === 'id'">
              <span> {{ props.row.id }}</span><br>
              <span
                v-if="props.row.is_an_exchange"
                style="color:red ; font-weight: bold;"
                aria-label="Colis d'échange"
              > Échange </span>
            </span>
            <!-- Column: Action -->
            <span v-else-if="props.column.field === 'action'">
              <span>
                <feather-icon
                  :id="`invoice-row-${props.row.id}-show-icon`"
                  icon="DeleteIcon"
                  color="red"
                  class="cursor-pointer mr-1"
                  size="16"
                  @click="resetItem(props.row)"
                />
                <b-tooltip
                  title="Annuler"
                  class="cursor-pointer"
                  :target="`invoice-row-${props.row.id}-show-icon`"
                />

              </span>
            </span>

            <!-- Column: Common -->

            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>

          <!-- pagination -->
          <template
            slot="pagination-bottom"
            slot-scope="props"
          >
            <div class="d-flex justify-content-between flex-wrap">
              <div class="d-flex align-items-center mb-0 mt-1">
                <span class="text-nowrap">
                  Affichage 1 à
                </span>
                <b-form-select
                  v-model="pageLength"
                  :options="['50','100','500']"
                  class="mx-1"
                  aria-label="Nombre de lignes par page"
                  @input="(value)=>props.perPageChanged({currentPerPage:value})"
                />
                <span class="text-nowrap "> de {{ props.total }} résultats trouvés </span>
              </div>
              <div>
                <b-pagination
                  :value="1"
                  :total-rows="props.total"
                  :per-page="pageLength"
                  first-number
                  last-number
                  align="right"
                  prev-class="prev-item"
                  next-class="next-item"
                  class="mt-1 mb-0"
                  @input="(value)=>props.pageChanged({currentPage:value})"
                >
                  <template #prev-text>
                    <feather-icon
                      icon="ChevronLeftIcon"
                      size="18"
                    />
                  </template>
                  <template #next-text>
                    <feather-icon
                      icon="ChevronRightIcon"
                      size="18"
                    />
                  </template>
                </b-pagination>
              </div>
            </div>
          </template>

        </vue-good-table>

        <b-form-group class="d-flex justify-content-end">
          <b-button
            variant="gradient-primary"
            class="ml-1 d-inline-block mt-1"
            :class="{ 'disabled-cursor': isLoading || !isReturnVoucherValid }"
            :disabled="isLoading || !isReturnVoucherValid"
            aria-label="Enregistrer le bon de remboursement logistique"
            @click="addReturnVoucher"
          >
            <feather-icon icon="PlusIcon" />
            <span class="ml-1">Enregistrer</span>
          </b-button>
        </b-form-group>
      </b-card>

    </div>
  </div>
</template>

<script>
import {
  BCard, BButton, BPagination, BFormGroup, BFormInput, BInputGroup, BInputGroupPrepend, BFormSelect, BTooltip,

} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import Ripple from 'vue-ripple-directive'
import axios from 'axios'
import { orderMatchesReference } from '@/utils/orderReference'
import vSelect from 'vue-select'
import { ValidationProvider } from 'vee-validate'
import storeAuth from '@/store/store'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import ok from '@/assets/audio/ok.mp3'
import notexist from '@/assets/audio/notexist.mp3'
import isAnExchange from '@/assets/audio/exchange.mp3'

export default {
  components: {
    BButton,
    ValidationProvider,
    VueGoodTable,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BFormSelect,
    BPagination,
    BTooltip,
    vSelect,
    BCard,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      suppliers: [],
      supplier: '',
      date: '01-10-2021',
      status: '',
      pageLength: 100,
      columns: [
        {
          label: 'Action',
          field: 'action',
        },
        {
          label: 'N° BL',
          field: 'id',
          filterOptions: {
            enabled: true,
            placeholder: 'N° BL',
          },
        },
        {
          label: 'Statut du colis',
          field: 'status',
          filterOptions: {
            enabled: true,
            placeholder: 'Statut',
          },
        },
        {
          label: 'Client',
          field: 'name',
          filterOptions: {
            enabled: true,
            placeholder: 'Client',
          },
        },
        {
          label: 'Gouvernorat',
          field: 'governorate',
          filterOptions: {
            enabled: true,
            placeholder: 'Gouvernorat',
          },
        },
      ],
      orders: [],
      selected: [],
      searchTerm: '',
      ref: '',
      user: {},
      isLoading: false,
      ok,
      notexist,
      isAnExchange,
    }
  },
  computed: {
    isReturnVoucherValid() {
      return (
        this.supplier.id !== ''
        && this.selected.length > 0
      )
    },
    processedOrders() {
      return this.orders.map(order => {
        const processedOrder = { ...order }

        if (order.status === 2) {
          processedOrder.status = 'colis au depot'
        } else if (order.status === 5) {
          processedOrder.status = 'Retour au fournisseur'
        }

        return processedOrder
      })
    },
  },
  created() {
    this.user = storeAuth.state.user
    this.getSuppliers()
  },
  mounted() {
    this.$refs.select.focus()
  },
  methods: {
    async playAudio(payload) {
      const voice = new Audio(payload)
      voice.addEventListener('canplaythrough', () => {
        voice.play()
      })
    },
    async withTitle(text) {
      await this.$swal({
        title: 'Alerte',
        icon: 'warning',
        text,
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      })
      this.ref = ''
    },
    async checkOrder() {
      const result = this.orders.find(element => orderMatchesReference(element, this.ref))
      if (result) {
        if (result.is_an_exchange) {
          await this.playAudio(isAnExchange)
        }
        await this.addItem(result)
        this.ref = ''
        await this.playAudio(ok)
      } else {
        await this.withTitle('Colis non exist')
        await this.playAudio(notexist)
      }
    },
    async getOrders() {
      if (this.supplier !== '') {
        this.isLoading = true
        try {
          const { data } = await axios.get(`api/orders/get-orders-allowed-reimbursed/${this.supplier.id}/`)
          this.orders = data
          this.selected = []
        } catch (error) {
          this.showToast('danger', 'top-center', `Erreur lors du chargement des commandes: ${error.response?.data?.message || error.message}`)
          this.orders = []
          this.selected = []
        }
        this.isLoading = false
      }
    },
    async addReturnVoucher() {
      // Prepare the simplified payload according to the updated backend API
      const obj = {
        supplier: this.supplier.id,
        orders: this.selected.map(order => ({ id: order.id })), // Only send order IDs
      }

      try {
        this.isLoading = true
        const response = await axios.post('/api/return-vouchers/create-reimbursed-voucher/', obj)
        const responseData = response.data

        // Handle the response which should now be a JSON object with success, message, and id
        if (responseData.success) {
          this.showToast('success', 'top-center', responseData.message)
          this.$router.push({ name: 'bon-a-rembourser-show', params: { id: responseData.id } })
        } else {
          this.showToast('danger', 'top-center', responseData.message || 'Erreur lors de la création du bon de remboursement')
        }
        this.isLoading = false
      } catch (error) {
        // Handle error response
        const errorMessage = error.response?.data?.message || error.message || 'Erreur lors de la création du bon de remboursement'
        this.showToast('danger', 'top-center', errorMessage)
        this.isLoading = false
      }
    },
    async getSuppliers() {
      this.isLoading = true
      const { data } = await axios.get('api/auth/users/suppliers/')
      this.suppliers = Array.isArray(data) ? data : []
      this.isLoading = false
    },
    async addItem(item) {
      this.selected.push(item)
      const i = this.orders.findIndex(element => {
        if (element.supplier.id === 504) {
          return element.bestway_barcode === item.bestway_barcode
        }
        return element.id === item.id
      })
      this.orders.splice(i, 1)
    },
    async resetItem(item) {
      this.orders.push(item)
      const i = this.selected.findIndex(element => {
        if (element.supplier.id === 504) {
          return element.bestway_barcode === item.bestway_barcode
        }
        return element.id === item.id
      })
      this.selected.splice(i, 1)
    },

    showToast(variant, position, text) {
      this.$toast(
        {
          component: ToastificationContent,
          props: {
            title: 'Notification',
            icon: 'InfoIcon',
            text,
            variant,
          },
        },
        {
          position,
        },
      )
    },
  },
}
</script>

<style lang="scss" >
@import "@core/scss/vue/libs/vue-select.scss";
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
